// Which attachments are worth offering to shrink.
//
// 🚩 Separate from the modal that uses it, for the same reason `reply-recipients.ts` is: this is the
// rule that decides whether someone gets interrupted at all, and a rule is testable without a
// browser. (Node strips types from `.ts` but cannot parse `.tsx`, so a predicate living beside JSX
// is a predicate with no unit test.)

/** What the server can actually decode. 🔒 SVG is deliberately absent — sharp has no raster output
 *  for it, so offering it would promise a conversion that cannot happen. */
export const IMAGE_TYPES = /^image\/(jpeg|png|webp|gif|tiff|avif|heic|heif)$/i;

/** Below this there is nothing to win and an interruption to lose. */
export const WORTH_OPTIMIZING_BYTES = 200 * 1024;

export function isOptimizableImage(a: { contentType: string; size: number }): boolean {
  return IMAGE_TYPES.test(a.contentType) && a.size >= WORTH_OPTIMIZING_BYTES;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * A content type for a file the browser would not name.
 *
 * 🔴 Chrome on Linux and Windows reports `file.type === ''` for `.heic`: there is no registered
 * MIME type for it on those platforms. Since iOS Safari transcodes HEIC to JPEG on upload, the
 * desktop drag is the main way a real HEIC ever reaches the server — so without this fallback the
 * HEIC support behind it would almost never run.
 *
 * 🚩 Only consulted when the browser gives nothing. A browser-supplied type still wins, and the
 * server re-reads the real format from the bytes regardless; this only decides whether to OFFER.
 */
export function guessTypeFromName(filename: string): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(filename || ''));
  const ext = (m ? m[1] : '').toLowerCase();
  const byExt: Record<string, string> = {
    heic: 'image/heic', heif: 'image/heif', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    png: 'image/png', webp: 'image/webp', gif: 'image/gif', tif: 'image/tiff',
    tiff: 'image/tiff', avif: 'image/avif',
  };
  return byExt[ext] || 'application/octet-stream';
}

// The focus-trap helpers used to live here. They moved to `lib/focus-trap.ts` when ui-dialogs.tsx
// started sharing them — import from there, not from this module.

/** The read ceiling, as a multiple of the send cap. See `planAttachment`. */
export const READ_CEILING_MULTIPLE = 8;

export interface AttachCandidate { contentType: string; size: number }

export type AttachPlan =
  | { action: 'reject'; reason: 'nothing-shrinkable' | 'too-much-to-read' }
  | { action: 'attach' }
  | { action: 'offer'; required: boolean };

/**
 * What to do with a selection the user just picked.
 *
 * 🔴 [Brian 2026-10-01] "I tried uploading 39mb of files, but after the conversion would've been
 * much smaller." The cap used to be judged on the ORIGINAL bytes, and the selection was refused
 * before the one step that would have made it fit. The cap is now judged on what will actually be
 * SENT — so an over-cap selection that contains shrinkable images becomes a REQUIRED conversion
 * rather than a refusal, and the real check happens afterwards in `fitsAfterConversion`.
 *
 * 🚩 Only images can shrink. A selection over the cap on its documents alone is still refused here,
 * because no amount of converting will help it.
 */
export function planAttachment(
  existingBytes: number,
  incoming: AttachCandidate[],
  cap: number,
): AttachPlan {
  const incomingBytes = incoming.reduce((sum, f) => sum + f.size, 0);
  // 🔒 A bound on what is READ, not on what is sent: base64 holds the whole selection in memory
  // before anything uploads, so a stray multi-gigabyte pick should not be attempted at all.
  if (incomingBytes > READ_CEILING_MULTIPLE * cap) return { action: 'reject', reason: 'too-much-to-read' };

  const overCap = existingBytes + incomingBytes > cap;
  const shrinkable = incoming.filter(isOptimizableImage);
  if (overCap && shrinkable.length === 0) return { action: 'reject', reason: 'nothing-shrinkable' };
  if (shrinkable.length > 0) return { action: 'offer', required: overCap };
  return { action: 'attach' };
}

/** Whether the converted set actually fits. The honest check, run after conversion. */
export function fitsAfterConversion(existingBytes: number, converted: AttachCandidate[], cap: number): boolean {
  return existingBytes + converted.reduce((sum, a) => sum + a.size, 0) <= cap;
}
